import { toUpper, get } from "lodash"
import { FACTOR_SENSITIVITY_LOOKBACK_DAYS } from "helpers/factorSensitivity"
import { percentageFormatter } from "helpers/formatters/numbers"
import { generateQueryKey } from "connected-components/dashboard/helpers/query-key"
import { isIntradaySimulationEligible } from "../helpers"
import { getIntradayBatchMoment } from "helpers/intraday"

function getFactorSensitivitySubquery(dateField) {
  return `factorSensitivity(
        type: $factorSensitivityType
        horizon: $horizon
        stdDev: $stdDev
        days: $days
      ) {
        ${dateField}
        factors {
          id
          name
          category
          sensitivity
        }
      }`
}

function buildQuery(shouldUseIntradayQuery) {
  if (shouldUseIntradayQuery) {
    // IntradayFactorSensitivity exposes `timestamp: DateTime!`, not `date` — alias it so downstream select stays uniform
    const factorSensitivitySubquery = getFactorSensitivitySubquery("date: timestamp")
    return `#multi-date-factor-sensitivity
query MultiDateFactorSensitivityQuery($modelId: String!, $positionSet: PositionSetInput!, $base: [PositionSetInput], $on: Date!, $factorSensitivityType: FactorSensitivityType!, $stdDev: PositiveFloat, $days: PositiveInt, $horizon: FactorSensitivityTimeHorizon!) {
  model(id: $modelId) {
    simulation:intradaySimulation(positionSet: $positionSet, base: $base, on: $on) {
      ${factorSensitivitySubquery}
    }
  }
}`
  }

  const factorSensitivitySubquery = getFactorSensitivitySubquery("date")
  return `#multi-date-factor-sensitivity
query MultiDateFactorSensitivityQuery($modelId: String!, $positionSet: PositionSetInput!, $base: [PositionSetInput], $from: Date!, $to: Date!, $factorSensitivityType: FactorSensitivityType!, $stdDev: PositiveFloat, $days: PositiveInt, $horizon: FactorSensitivityTimeHorizon!) {
  model(id: $modelId) {
    simulation(positionSet: $positionSet, base: $base, from: $from, to: $to, interval: AUTO) {
      ${factorSensitivitySubquery}
    }
  }
}`
}

const DAY_BASED_QUERY = buildQuery(false)
const INTRADAY_QUERY = buildQuery(true)

// intradaySimulationBatch returns plain `FactorSensitivityDate` (a `date`, not a `timestamp`) same
// as the day-based branch.
const BATCH_TREND_QUERY = `#multi-date-factor-sensitivity-batch
query MultiDateFactorSensitivityBatchQuery($modelId: String!, $positionSet: PositionSetInput!, $base: [PositionSetInput], $on: Date!, $batchId: Id, $factorSensitivityType: FactorSensitivityType!, $stdDev: PositiveFloat, $days: PositiveInt, $horizon: FactorSensitivityTimeHorizon!) {
  model(id: $modelId) {
    simulation:intradaySimulationBatch(positionSet: $positionSet, base: $base, on: $on, batchId: $batchId) {
      ${getFactorSensitivitySubquery("date")}
    }
  }
}`

function mapFactorSensitivityRow(dateData) {
  // A live tick can arrive before the server has finished computing this row's factors.
  return (dateData.factors ?? []).reduce((acc, { id, sensitivity }) => {
    acc[id] = sensitivity
    return acc
  }, {})
}

// One query per committed batch; the template merges these with the live-tick query (graph/helpers.js extractDatesFromEachSeries).
export function createFactorSensitivityIntradayBatchTrendQueries(modelId, positionSet, base, batches, factorSensitivityType) {
  const sharedVariables = {
    modelId,
    positionSet,
    base,
    horizon: "MONTHLY",
    days: factorSensitivityType === "historical" ? FACTOR_SENSITIVITY_LOOKBACK_DAYS : undefined,
    factorSensitivityType: toUpper(factorSensitivityType),
  }

  return batches.map(batch => {
    const key = generateQueryKey("factorSensitivity.multiDate.batch", {
      factorSensitivityType, modelId, positionSet, base, on: batch.date, batchId: batch.id,
    })

    return {
      key,
      skipInvalidation: true,
      query: BATCH_TREND_QUERY,
      variables: { ...sharedVariables, on: batch.date, batchId: batch.id },
      select: (graphqlResult) => {
        // intradaySimulationBatch is single-instant — one row in, one point out.
        const [row] = get(graphqlResult, "data.model.simulation.factorSensitivity", [])
        const batchMoment = getIntradayBatchMoment(batch)
        return {
          values: {
            formatter: percentageFormatter,
            // Drop rather than chart a malformed date/asOf — an Invalid Date would corrupt sort order.
            dates: row && batchMoment.isValid() ? [{
              date: batchMoment.toISOString(),
              batchId: batch.id,
              ...mapFactorSensitivityRow(row),
            }] : [],
          },
        }
      },
    }
  })
}

export function createFactorSensitivityMultiDateQuery(modelId, positionSet, base, from, to, factorSensitivityType, isIntradayMode, options = {}) {
  const shouldUseIntradaySimulation = isIntradaySimulationEligible(isIntradayMode, from, to, options.batchId, options.lastIntradayBatchId)

  const key = generateQueryKey("factorSensitivity.multiDate", {
    factorSensitivityType, modelId, positionSet, base, from, to, intraday: shouldUseIntradaySimulation || undefined,
  })

  const sharedVariables = {
    modelId,
    positionSet,
    base,
    horizon: "MONTHLY",
    days: factorSensitivityType === "historical" ? FACTOR_SENSITIVITY_LOOKBACK_DAYS : undefined,
    factorSensitivityType: toUpper(factorSensitivityType),
  }

  const variables = shouldUseIntradaySimulation
    ? { ...sharedVariables, on: from }
    : { ...sharedVariables, from, to }

  return {
    key,
    query: shouldUseIntradaySimulation ? INTRADAY_QUERY : DAY_BASED_QUERY,
    variables,
    select: (graphqlResult) => {
      const factorSensitivity = get(graphqlResult, "data.model.simulation.factorSensitivity", [])
      return ({
        values: {
          formatter: percentageFormatter,
          dates: factorSensitivity.map(({ date, ...dateData }) => ({
            date,
            ...mapFactorSensitivityRow(dateData),
          })),
        },
      })
    },
  }
}
